/*--------------------------------------------------------------------
   HEY NOW RECORDS — Custom JS
   All custom behavior added on top of the Horizon theme lives here.
   If something breaks, the bug is almost certainly in this file.
--------------------------------------------------------------------*/

/*-------- HEADER SCROLL STATE --------
   Toggles [data-scrolled] on <header-component> based on scroll
   position. custom.css uses this to switch the header from
   transparent (at the top) to translucent dark + blur. */
const initHeaderScroll = () => {
  const header = document.querySelector('header-component');
  if (!header) return;

  const SCROLL_THRESHOLD = 8;
  let raf = null;

  const update = () => {
    raf = null;
    if (window.scrollY > SCROLL_THRESHOLD) {
      header.setAttribute('data-scrolled', '');
    } else {
      header.removeAttribute('data-scrolled');
    }
  };

  const onScroll = () => {
    if (raf !== null) return;
    raf = requestAnimationFrame(update);
  };

  update();
  window.addEventListener('scroll', onScroll, { passive: true });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderScroll);
} else {
  initHeaderScroll();
}

/*-------- MEGA MENU: hover safety triangle (desktop) --------
   Holds the open mega-menu panel while the pointer travels diagonally
   toward it, so you can swing from SHOP down to the far-left / far-right
   tiles without the menu closing on you.

   It works WITH Horizon's menu JS instead of against it: we wrap
   header-menu's `deactivate` and DEFER the close while the pointer is
   genuinely aiming at the open panel. We never keep a closed panel
   interactive (that fights Horizon's pointer reconciliation). When the
   pointer reaches the panel, reverses, or stalls, Horizon does the real
   close exactly as it normally would. */
const initMegaMenuSafetyTriangle = () => {
  // Hover-driven menu only exists on fine pointers (no touch / mobile drawer).
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  customElements.whenDefined('header-menu').then(() => {
    const proto = customElements.get('header-menu').prototype;
    if (proto.__hnrTriangleWrapped) return;
    proto.__hnrTriangleWrapped = true;

    const originalDeactivate = proto.deactivate;
    // Temporary diagnostics: remove once the diagonal hover is confirmed working.
    console.log('[HNR menu] safety triangle installed');

    // Track the last two pointer samples for a movement vector.
    const p = { x: 0, y: 0, px: 0, py: 0 };
    document.addEventListener(
      'pointermove',
      (e) => {
        p.px = p.x;
        p.py = p.y;
        p.x = e.clientX;
        p.y = e.clientY;
      },
      { capture: true, passive: true }
    );

    // Find the currently-open menu item + its panel from the DOM (no privates).
    const getOpen = (comp) => {
      const link = comp.querySelector('[ref="menuitem"][aria-expanded="true"]');
      if (!link) return null;
      const li = link.closest('.menu-list__list-item');
      const panel = li && li.querySelector('[ref="submenu[]"]');
      if (!li || !panel) return null;
      return { li, link, linkRect: link.getBoundingClientRect() };
    };

    // Aiming = moving downward (toward the panel below the nav row) while the
    // pointer is still near the nav row. Keyed off the LINK rect, not the panel,
    // because the panel is positioned/clipped in ways that make its box unreliable.
    const isAiming = (linkRect) => {
      const vy = p.y - p.py;
      if (vy <= 0.5) return false; // not moving downward
      if (p.y > linkRect.bottom + 90) return false; // already well below the nav row
      return true;
    };

    let holdTimer = null;
    let moveHandler = null;

    const releaseHold = () => {
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      if (moveHandler) {
        document.removeEventListener('pointermove', moveHandler, true);
        moveHandler = null;
      }
    };

    const fakeLeave = (comp) => ({ target: comp, relatedTarget: null, type: 'pointerleave' });

    proto.deactivate = function (event) {
      const open = getOpen(this);

      // Nothing open, or the pointer isn't aiming at the panel -> close normally.
      if (!open || !isAiming(open.linkRect)) {
        console.log('[HNR menu] deactivate -> close now', {
          open: !!open,
          vy: (p.y - p.py).toFixed(1),
        });
        releaseHold();
        return originalDeactivate.call(this, event);
      }

      console.log('[HNR menu] deactivate -> HOLD (aiming at panel)', { vy: (p.y - p.py).toFixed(1) });

      // Pointer is aiming at the panel: hold it open and watch the pointer.
      releaseHold();
      const comp = this;
      const heldLi = open.li;

      // Safety cap: never hold longer than this if the pointer just stalls.
      holdTimer = setTimeout(() => {
        releaseHold();
        const stillOpen = getOpen(comp);
        const el = document.elementFromPoint(p.x, p.y);
        if (stillOpen && stillOpen.li === heldLi && !(el && heldLi.contains(el))) {
          console.log('[HNR menu] hold cap reached -> close');
          originalDeactivate.call(comp, fakeLeave(comp));
        }
      }, 500);

      moveHandler = () => {
        const cur = getOpen(comp);
        // Active item switched (moved to another nav item) -> let go, it manages itself.
        if (!cur || cur.li !== heldLi) return releaseHold();

        const el = document.elementFromPoint(p.x, p.y);
        // Reached the panel/link -> native hover holds it open now.
        if (el && heldLi.contains(el)) return releaseHold();

        // Pointer clearly reversed (heading back up) -> close now.
        if (p.y - p.py < -1) {
          releaseHold();
          originalDeactivate.call(comp, fakeLeave(comp));
          return;
        }
        // Otherwise still descending / stalled -> keep holding (cap timer guards).
      };
      document.addEventListener('pointermove', moveHandler, { capture: true, passive: true });

      // Skip the immediate close; the handlers above will close at the right time.
    };
  });
};
initMegaMenuSafetyTriangle();
